import type { IncomingMessage } from 'http';
import type { GetServerSidePropsContext } from 'next';

/**
 * Request header stamped by middleware when the host matches the "-preview"
 * domain pattern. Pages read this header instead of checking the hostname
 * directly because:
 *
 * - Middleware centralises the hostname resolution (including the
 *   `x-breitling-domain` CDN header fallback) in a single place rather
 *   than duplicating it in every getServerSideProps / getInitialProps.
 * - `_app.tsx` getInitialProps has a different context shape than GSSP;
 *   a uniform header keeps both resolve functions identical.
 * - If we ever adopt ISR / static generation, the header approach still
 *   works — middleware runs at the edge and stamps the header before the
 *   page is served from cache or regenerated.
 */
export const CONTENT_DRAFT_MODE_HEADER = 'x-content-draft-mode';

const PREVIEW_HOST_PATTERN = '-preview';

export function isContentDraftHost(hostname: string): boolean {
  return (
    hostname.toLowerCase().includes(PREVIEW_HOST_PATTERN) ||
    hostname.includes('www-stg')
  );
}

/**
 * Resolves content draft mode from the `x-content-draft-mode` header
 * set by middleware when the host matches the preview domain pattern.
 */
export function resolveContentDraftMode(
  context: GetServerSidePropsContext,
): boolean {
  return context.req?.headers?.[CONTENT_DRAFT_MODE_HEADER] === '1';
}

/**
 * Resolves content draft mode from a raw `IncomingMessage` (for use in
 * `_app.tsx` `getInitialProps` where a full GSSP context is unavailable).
 */
export function resolveContentDraftModeFromReq(
  req: IncomingMessage | undefined,
): boolean {
  if (!req) return false;
  return req.headers[CONTENT_DRAFT_MODE_HEADER] === '1';
}
